import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../../../core/utils/apiClient';

interface BreakdownComponent {
  name: string;
  weight: number;
  earned: number;
  filled?: number;
  total?: number;
  present?: boolean;
  missing?: string[];
}

interface BreakdownResponse {
  score: number;
  components: BreakdownComponent[];
}

const InfoIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="8" cy="8" r="6.5" />
    <line x1="8" y1="11" x2="8" y2="7" />
    <circle cx="8" cy="5" r="0.6" fill="currentColor" stroke="none" />
  </svg>
);

const Popover: React.FC<{
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
}> = ({ open, onClose, children, width = 320 }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      ref={ref}
      className="absolute z-20 mt-2 right-0 bg-white rounded-lg shadow-lg border border-gray-200 p-4 text-xs text-gray-700"
      style={{ width }}
    >
      {children}
    </div>
  );
};

const InfoButton: React.FC<{ ariaLabel: string; onClick: () => void }> = ({ ariaLabel, onClick }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    className="ml-1 text-gray-400 hover:text-gray-600 focus:outline-none"
  >
    <InfoIcon />
  </button>
);

const formatHumanField = (key: string) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

export const ProfileCompletenessInfo: React.FC<{ companyId: number }> = ({ companyId }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<BreakdownResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !data) {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/api/v1/companies/${companyId}/completeness-breakdown`);
        if (response.ok) {
          setData(await response.json());
        } else {
          setError('Failed to load breakdown');
        }
      } catch (err) {
        setError('Failed to load breakdown');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <span className="relative inline-block">
      <InfoButton ariaLabel="What this score means" onClick={handleToggle} />
      <Popover open={open} onClose={() => setOpen(false)} width={340}>
        <div className="font-semibold text-gray-900 mb-2">Profile completeness</div>
        <p className="text-gray-500 mb-3">
          Weighted across 6 dimensions of how much we know about the company.
        </p>
        {loading && <p className="text-gray-400">Loading…</p>}
        {error && <p className="text-red-600">{error}</p>}
        {data && (
          <div className="space-y-3">
            {data.components.map((c) => {
              const fraction = c.weight > 0 ? c.earned / c.weight : 0;
              return (
                <div key={c.name}>
                  <div className="flex justify-between items-baseline">
                    <span className="font-medium text-gray-800">{c.name}</span>
                    <span className="text-gray-500">
                      {c.earned.toFixed(1)} / {c.weight}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1">
                    <div
                      className="h-full bg-blue-500"
                      style={{ width: `${Math.min(fraction * 100, 100)}%` }}
                    />
                  </div>
                  {c.missing && c.missing.length > 0 && (
                    <div className="mt-1 text-gray-500">
                      Missing: {c.missing.map(formatHumanField).join(', ')}
                    </div>
                  )}
                  {c.present === false && (
                    <div className="mt-1 text-gray-500">Not yet run</div>
                  )}
                  {c.filled !== undefined && c.total !== undefined && c.missing?.length === 0 && c.present !== false && (
                    <div className="mt-1 text-gray-400">
                      {c.filled} / {c.total}
                      {c.filled >= c.total ? ' (full credit)' : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Popover>
    </span>
  );
};

export const EnrichmentScoreInfo: React.FC<{
  companyId: number;
  enrichmentScore: number | null | undefined;
  latestSessionScore: number | null | undefined;
  latestSessionStatus: string | null | undefined;
  scoringInProgress?: boolean;
}> = ({ companyId, enrichmentScore, latestSessionScore, latestSessionStatus, scoringInProgress }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const handleRerun = async () => {
    setSubmitting(true);
    setFeedback(null);
    try {
      const response = await api.post(
        `/api/v1/enrichment/companies/${companyId}/enrichment/start`,
        { question_categories: null }
      );
      if (response.ok) {
        setFeedback({ kind: 'ok', text: 'Enrichment started — score will update shortly.' });
      } else {
        const err = await response.json().catch(() => ({}));
        if (response.status === 409) {
          setFeedback({ kind: 'err', text: 'A session is already running.' });
        } else if (response.status === 400) {
          setFeedback({ kind: 'err', text: err.detail || err.error || 'No data sources to enrich. Upload documents or run web/LinkedIn enrichment first.' });
        } else {
          setFeedback({ kind: 'err', text: err.detail || err.error || 'Failed to start enrichment.' });
        }
      }
    } catch (err: any) {
      setFeedback({ kind: 'err', text: err.message || 'Failed to start enrichment.' });
    } finally {
      setSubmitting(false);
    }
  };

  const canRerun = !scoringInProgress && !submitting;

  return (
    <span className="relative inline-block">
      <InfoButton ariaLabel="What this score means" onClick={() => setOpen((v) => !v)} />
      <Popover open={open} onClose={() => setOpen(false)} width={320}>
        <div className="font-semibold text-gray-900 mb-2">Enrichment score</div>
        <p className="text-gray-500 mb-3">
          LLM-graded answer to “how well can we answer questions about this company from
          its documents and enrichment data?” Set when an enrichment session reaches
          the score threshold or the user finalizes via the form.
        </p>
        {enrichmentScore == null && latestSessionScore != null && (
          <div className="border-t border-gray-100 pt-2 mb-3">
            <div className="text-gray-700 mb-1">Latest session</div>
            <div className="flex justify-between">
              <span className="text-gray-500">Score</span>
              <span className="text-gray-900 font-medium">{latestSessionScore.toFixed(2)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Status</span>
              <span className="text-gray-900">{latestSessionStatus ?? '—'}</span>
            </div>
          </div>
        )}
        {enrichmentScore == null && latestSessionScore == null && (
          <p className="text-gray-500 mb-3">No enrichment session has run yet.</p>
        )}
        <button
          type="button"
          onClick={handleRerun}
          disabled={!canRerun}
          className="w-full px-3 py-1.5 text-xs font-medium rounded-md text-white disabled:opacity-50 flex items-center justify-center gap-1.5"
          style={{ backgroundColor: '#9333EA' }}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M2 8a6 6 0 1 0 1.76-4.24" />
            <path d="M2 2v4h4" />
          </svg>
          {submitting ? 'Starting…' : scoringInProgress ? 'In progress…' : 'Re-run enrichment'}
        </button>
        {feedback && (
          <p
            className="mt-2 text-xs"
            style={{ color: feedback.kind === 'ok' ? '#059669' : '#DC2626' }}
          >
            {feedback.text}
          </p>
        )}
      </Popover>
    </span>
  );
};
